<script setup>
import { WButton, WTextfield, WTitle } from '@alice-health/wonderland-vue'
import { computed, inject, ref, watch } from 'vue'
import JsonViewer from 'vue-json-viewer'

import { SelectSearch } from '@/components/shared/atoms'
import { STAFF_STATUS } from '@/models/channels'
import DatabaseFactory from '@/wrappers/database'
import { useStore } from '@/wrappers/store'

import { transformDates } from './DebugUtils'

const { mapActions } = useStore()
const { getStaffsById, searchStaff } = mapActions('staff')

const confirm = inject('confirm')
const notify = inject('notify')

const staffSelected = ref(undefined)
const staffDocument = ref(undefined)
const staffId = ref(undefined)

const isStaffOffline = computed(() => {
  return (
    staffDocument.value &&
    staffDocument.value.status === STAFF_STATUS.UNAVAILABLE
  )
})
watch(staffSelected, value => {
  if (value) staffId.value = value.id
  else staffId.value = undefined
})
const getStaffById = async () => {
  staffSelected.value = await getStaffsById(staffId.value)
}
const getStaffByName = async (query, signal) => {
  return await searchStaff({
    query,
    options: { signal }
  })
}
const setStaffOffline = async () => {
  const confirmed = await confirm({
    title: 'Tem certeza que deseja definir o status para Offline?',
    description:
      'Isso mudará o status do profissional para offline e ele não poderá receber nem ver novos chats'
  })

  if (confirmed) {
    await updateStaff(staffId.value)
    getColletionData()
  }
}

const updateStaff = async staffId => {
  const database = new DatabaseFactory(`staff/${staffId}`).getWriteDatabase()
  try {
    await database.update({
      status: STAFF_STATUS.UNAVAILABLE
    })

    notify({
      description: 'HP atualizado com sucesso!',
      duration: 6000
    })
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e)

    notify({
      description: 'Ocorreu um erro ao atualizar o HP',
      duration: 6000
    })
  }
}

const getColletionData = async getChannels => {
  const database = new DatabaseFactory(
    `staff/${staffId.value}`
  ).getReadDatabase()
  const staffDoc = await database.getOne()

  const transformedStaff = transformDates(staffDoc.data())

  staffDocument.value = transformedStaff

  if (getChannels) {
    getStaffChannels()
  }
}
const getStaffChannels = async () => {
  const database = new DatabaseFactory(
    `staff/${staffId.value}/channels`
  ).getReadDatabase()

  const channelsLight = {}
  const snapshot = await database.get()
  snapshot.docs.map(channelDoc => {
    const channel = channelDoc.data()
    const lastPreviewableMessage = channel.lastPreviewableMessage || {}
    delete lastPreviewableMessage.content

    channelsLight[channelDoc.id] = {
      ...channel,
      timeLastMessage: channel.timeLastMessage?.toDate(),
      waitingSince: channel.waitingSince?.toDate(),
      lastPreviewableMessage: lastPreviewableMessage
    }

    return channelsLight
  })
  staffDocument.value = {
    ...staffDocument.value,
    channels: channelsLight
  }
}
</script>

<template>
  <div class="debug__grid">
    <div class="debug__form">
      <WTitle size="small" class="debug__input">Staff</WTitle>
      <SelectSearch
        id="debug-staff-search"
        hide-icon
        :value="staffSelected"
        placeholder="Digite o nome do staff"
        value-label="fullName"
        value-detail="roleDescription"
        :search-function="getStaffByName"
        class="debug__input"
        @input="staffSelected = $event"
      />
      <WTextfield
        v-model="staffId"
        label="Staff Id"
        class="debug__input"
        @change="getStaffById"
      />
      <WButton
        block
        :disabled="!staffId"
        class="debug__cta"
        @click="getColletionData(false)"
      >
        Buscar dados na collection de staff
      </WButton>
      <WButton
        block
        :disabled="!staffId"
        class="debug__cta"
        @click="getColletionData(true)"
      >
        Buscar dados na collection de staff + canais
      </WButton>
    </div>
    <div v-if="staffDocument" class="debug__results">
      <JsonViewer
        class="debug__json"
        :value="staffDocument"
        :expand-depth="2"
      />
      <WButton
        v-if="!isStaffOffline"
        block
        class="debug__cta"
        @click="setStaffOffline"
      >
        Alterar status para offline
      </WButton>
    </div>
  </div>
</template>
